import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { sbFetch } from "../utils/supabaseClient";
import { useToast } from "./Toast";

const ConsultationActivityReport = forwardRef((props, ref) => {
  const { error } = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedResponse, setSelectedResponse] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useImperativeHandle(ref, () => ({
    refreshData: loadData
  }));

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await sbFetch("GET", "consultation_activity?order=created_at.desc&limit=10000");
      setData(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Error loading consultation data:", err);
      error("Failed to load consultation activity data");
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data.filter((item) => {
    const itemDate = new Date(item.created_at);
    const startDate = dateRange.start ? new Date(dateRange.start) : null;
    const endDate = dateRange.end ? new Date(dateRange.end) : null;
    const responseNormalized = item.response || "later"; // Treat null as "later"

    if (startDate && itemDate < startDate) return false;
    if (endDate && itemDate > new Date(endDate + "T23:59:59")) return false;
    if (selectedTopic && item.topic !== selectedTopic) return false;
    if (selectedResponse && responseNormalized !== selectedResponse) return false;

    return true;
  });

  // Calculate metrics
  const totalConsultations = filteredData.length;
  const yesResponses = filteredData.filter(d => !d.response || d.response === "yes" ? d.response === "yes" : false).length;
  const laterResponses = filteredData.filter(d => !d.response || d.response === "later").length;

  // Get unique topics
  const uniqueTopics = [...new Set(data.map(d => d.topic).filter(Boolean))];

  // Prepare timeline data (by day)
  const timelineMap = {};
  const topicTrendMap = {};

  filteredData.forEach(item => {
    const date = new Date(item.created_at).toLocaleDateString();
    const topic = item.topic || "Unknown";

    timelineMap[date] = (timelineMap[date] || 0) + 1;

    if (!topicTrendMap[topic]) {
      topicTrendMap[topic] = {};
    }
    topicTrendMap[topic][date] = (topicTrendMap[topic][date] || 0) + 1;
  });

  const timelineData = Object.entries(timelineMap)
    .sort(([a], [b]) => new Date(a) - new Date(b))
    .map(([date, count]) => ({ date, consultations: count }));

  // Response breakdown
  const responseData = [
    { name: "Yes", value: yesResponses, fill: "#22C55E" },
    { name: "Later", value: laterResponses, fill: "#F59E0B" }
  ];

  // Word cloud data - calculate font sizes based on frequency
  const topicFrequency = {};
  filteredData.forEach(item => {
    if (item.topic) {
      topicFrequency[item.topic] = (topicFrequency[item.topic] || 0) + 1;
    }
  });

  const maxFreq = Math.max(...Object.values(topicFrequency), 1);
  const wordCloudItems = Object.entries(topicFrequency)
    .sort((a, b) => b[1] - a[1])
    .map(([topic, count]) => ({
      topic,
      count,
      size: 12 + (count / maxFreq) * 32 // Font size between 12-44px
    }));

  if (loading) return <div className="loading">Loading consultation data...</div>;

  return (
    <div style={{ padding: "20px", color: "#e0e0e0" }}>
      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        <div style={{ background: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", padding: "16px" }}>
          <div style={{ fontSize: "12px", color: "#7A9CC4", marginBottom: "8px" }}>Total Consultations</div>
          <div style={{ fontSize: "28px", fontWeight: "bold", color: "#5B9EF0" }}>{totalConsultations}</div>
        </div>
        <div style={{ background: "rgba(34, 197, 94, 0.1)", border: "1px solid rgba(34, 197, 94, 0.2)", borderRadius: "8px", padding: "16px" }}>
          <div style={{ fontSize: "12px", color: "#7A9CC4", marginBottom: "8px" }}>Yes Responses</div>
          <div style={{ fontSize: "28px", fontWeight: "bold", color: "#22C55E" }}>{yesResponses}</div>
        </div>
        <div style={{ background: "rgba(245, 158, 11, 0.1)", border: "1px solid rgba(245, 158, 11, 0.2)", borderRadius: "8px", padding: "16px" }}>
          <div style={{ fontSize: "12px", color: "#7A9CC4", marginBottom: "8px" }}>Later Responses</div>
          <div style={{ fontSize: "28px", fontWeight: "bold", color: "#F59E0B" }}>{laterResponses}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", padding: "16px", marginBottom: "24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
          <div>
            <label style={{ fontSize: "12px", color: "#7A9CC4", display: "block", marginBottom: "6px" }}>Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              style={{
                width: "100%",
                padding: "8px",
                background: "rgba(5, 14, 26, 0.8)",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                borderRadius: "6px",
                color: "#e0e0e0",
                boxSizing: "border-box"
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: "12px", color: "#7A9CC4", display: "block", marginBottom: "6px" }}>End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              style={{
                width: "100%",
                padding: "8px",
                background: "rgba(5, 14, 26, 0.8)",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                borderRadius: "6px",
                color: "#e0e0e0",
                boxSizing: "border-box"
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: "12px", color: "#7A9CC4", display: "block", marginBottom: "6px" }}>Topic</label>
            <select
              value={selectedTopic || ""}
              onChange={(e) => setSelectedTopic(e.target.value || null)}
              style={{
                width: "100%",
                padding: "8px",
                background: "rgba(5, 14, 26, 0.8)",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                borderRadius: "6px",
                color: "#e0e0e0",
                boxSizing: "border-box"
              }}
            >
              <option value="">All Topics</option>
              {uniqueTopics.map(topic => (
                <option key={topic} value={topic}>{topic}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: "12px", color: "#7A9CC4", display: "block", marginBottom: "6px" }}>Response</label>
            <select
              value={selectedResponse || ""}
              onChange={(e) => setSelectedResponse(e.target.value || null)}
              style={{
                width: "100%",
                padding: "8px",
                background: "rgba(5, 14, 26, 0.8)",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                borderRadius: "6px",
                color: "#e0e0e0",
                boxSizing: "border-box"
              }}
            >
              <option value="">All Responses</option>
              <option value="yes">Yes</option>
              <option value="later">Later</option>
            </select>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "24px" }}>
        {/* Timeline Chart */}
        <div style={{ background: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", padding: "16px" }}>
          <h3 style={{ margin: "0 0 16px 0", color: "#5B9EF0" }}>Consultations Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timelineData}>
              <CartesianGrid stroke="rgba(59, 130, 246, 0.1)" />
              <XAxis dataKey="date" stroke="#7A9CC4" style={{ fontSize: "12px" }} />
              <YAxis stroke="#7A9CC4" style={{ fontSize: "12px" }} />
              <Tooltip contentStyle={{ background: "rgba(5, 14, 26, 0.9)", border: "1px solid rgba(59, 130, 246, 0.3)", borderRadius: "6px", color: "#e0e0e0" }} />
              <Line type="monotone" dataKey="consultations" stroke="#5B9EF0" dot={{ fill: "#5B9EF0" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Response Distribution */}
        <div style={{ background: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", padding: "16px" }}>
          <h3 style={{ margin: "0 0 16px 0", color: "#5B9EF0" }}>Response Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={responseData} cx="50%" cy="50%" labelLine={false} label={({ percent }) => `${(percent * 100).toFixed(0)}%`} outerRadius={80}>
                {responseData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: "rgba(5, 14, 26, 0.9)", border: "1px solid rgba(59, 130, 246, 0.3)", borderRadius: "6px", color: "#e0e0e0" }} />
              <Legend verticalAlign="bottom" height={36} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Word Cloud */}
      <div style={{ background: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", padding: "24px", marginBottom: "24px" }}>
        <h3 style={{ margin: "0 0 16px 0", color: "#5B9EF0" }}>Topics Word Cloud</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
          {wordCloudItems.length > 0 ? (
            wordCloudItems.map((item, idx) => (
              <div
                key={idx}
                style={{
                  fontSize: `${item.size}px`,
                  color: `hsl(200, 70%, ${50 + (item.count / maxFreq) * 30}%)`,
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  padding: "8px 12px",
                  borderRadius: "4px"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "rgba(59, 130, 246, 0.1)";
                  e.target.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.transform = "scale(1)";
                }}
                onClick={() => setSelectedTopic(item.topic)}
                title={`${item.topic}: ${item.count} consultations`}
              >
                {item.topic}
              </div>
            ))
          ) : (
            <div style={{ color: "#7A9CC4" }}>No topics found</div>
          )}
        </div>
      </div>

      {/* Data Table */}
      <div style={{ background: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", padding: "16px", overflowX: "auto" }}>
        <h3 style={{ margin: "0 0 16px 0", color: "#5B9EF0" }}>Consultation Activity Details</h3>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(59, 130, 246, 0.2)" }}>
              <th style={{ padding: "12px", textAlign: "left", color: "#7A9CC4", fontWeight: "600" }}>Email</th>
              <th style={{ padding: "12px", textAlign: "left", color: "#7A9CC4", fontWeight: "600" }}>Topic</th>
              <th style={{ padding: "12px", textAlign: "left", color: "#7A9CC4", fontWeight: "600" }}>Event</th>
              <th style={{ padding: "12px", textAlign: "left", color: "#7A9CC4", fontWeight: "600" }}>Response</th>
              <th style={{ padding: "12px", textAlign: "left", color: "#7A9CC4", fontWeight: "600" }}>Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.slice(0, 100).map((item) => (
              <tr key={item.id} style={{ borderBottom: "1px solid rgba(59, 130, 246, 0.1)" }}>
                <td style={{ padding: "12px", color: "#e0e0e0" }}>{item.email}</td>
                <td style={{ padding: "12px", color: "#e0e0e0" }}>{item.topic || "-"}</td>
                <td style={{ padding: "12px", color: "#e0e0e0" }}>{item.event}</td>
                <td style={{ padding: "12px" }}>
                  <span style={{
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    fontWeight: "600",
                    background: item.response === "yes" ? "rgba(34, 197, 94, 0.2)" : "rgba(245, 158, 11, 0.2)",
                    color: item.response === "yes" ? "#22C55E" : "#F59E0B"
                  }}>
                    {item.response || "later"}
                  </span>
                </td>
                <td style={{ padding: "12px", color: "#7A9CC4" }}>
                  {new Date(item.created_at).toLocaleDateString()} {new Date(item.created_at).toLocaleTimeString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredData.length > 100 && (
          <div style={{ marginTop: "12px", color: "#7A9CC4", fontSize: "12px" }}>
            Showing 100 of {filteredData.length} records
          </div>
        )}
      </div>
    </div>
  );
});

export default ConsultationActivityReport;
